import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { SearchIcon, XIcon } from '../components/icons';
import { Input, Loading } from '../components/ui';
import { timecode } from '../lib/format';
import type { VideoSummary } from '../lib/types';
import { qk } from '../queries/keys';
import { fetchLibrary } from '../queries/videos';

/**
 * Picking a recording to drop into this one as a clip. Once picked, the clip
 * is a piece on the editor's one timeline like any other, and is moved and
 * trimmed there; nothing else about stitching lives here.
 */

// ---------------------------------------------------------------------------
// Picking a recording to add.

/**
 * The same search as the intro and outro pickers on the branding page, and
 * for the same reason: the search runs on the API rather than over what was
 * already fetched, so a library of three hundred recordings is all of it,
 * and the transcript is searched too.
 */
export function ClipPicker({
  excludeVideoId,
  onPick,
  onClose,
}: {
  excludeVideoId: string;
  onPick: (video: VideoSummary) => void;
  onClose: () => void;
}) {
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(term.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [term]);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const library = useQuery({
    queryKey: qk.videos(`stitch:${query}`),
    queryFn: () => fetchLibrary({ limit: 100, q: query || undefined }),
    staleTime: 30_000,
  });
  // A clip has to be something that will actually play where it is dropped:
  // this recording itself, a voice note, one still processing or one behind
  // the free-tier window would each be a hole in the middle of the finished
  // video.
  const choices = (library.data?.videos ?? []).filter(
    (video: VideoSummary) =>
      video.id !== excludeVideoId &&
      video.status === 'ready' &&
      video.kind !== 'audio' &&
      !video.locked &&
      !video.deletedAt &&
      Boolean(video.durationSec),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/70 p-6 pt-20"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label="Add a clip"
        className="w-full max-w-lg rounded-lg border border-hairline bg-slate-900 p-4 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-h4 text-chalk">Add a clip</h2>
            <p className="mt-0.5 text-caption text-chalk-faint">
              A piece of another recording, dropped into this one. The one you pick is not touched.
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-chalk-dim hover:text-chalk"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="relative mt-3">
          <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-chalk-faint">
            <SearchIcon size={14} />
          </span>
          <Input
            type="search"
            className="pl-8"
            placeholder="Search your recordings"
            value={term}
            autoFocus
            spellCheck={false}
            onChange={(event) => setTerm(event.target.value)}
            aria-label="Search recordings to add as a clip"
          />
        </div>

        {library.isPending ? (
          <div className="mt-3">
            <Loading label="Loading your recordings" />
          </div>
        ) : choices.length === 0 ? (
          <p className="mt-4 text-caption text-chalk-faint">
            {query
              ? `Nothing matches “${query}”.`
              : 'Nothing else to add yet. Record the piece first, then come back and drop it in.'}
          </p>
        ) : (
          <ul className="mt-3 max-h-80 space-y-1 overflow-y-auto pr-1">
            {choices.map((video) => (
              <li key={video.id}>
                <button
                  type="button"
                  className="flex w-full items-center gap-3 rounded p-1.5 text-left hover:bg-slate-800"
                  onClick={() => onPick(video)}
                >
                  <span className="h-10 w-16 shrink-0 overflow-hidden rounded bg-slate-950">
                    {video.posterUrl ? (
                      <img src={video.posterUrl} alt="" className="h-full w-full object-cover" />
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body text-chalk">{video.title}</span>
                    {video.durationSec ? (
                      <span className="text-caption tnum font-mono text-chalk-faint">
                        {timecode(video.durationSec)}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
